import { useEffect, useState } from "preact/hooks"
import { formatDistanceToNow, parseISO } from "date-fns"
import {
  AccountingSyncedCheckDetail,
  AccountingSyncedCheckSummary,
  AccountingSyncedCreditMemoDetail,
  AccountingSyncedCreditMemoSummary,
  AccountingSyncedCustomerDetail,
  AccountingSyncedCustomerSummary,
  AccountingSyncedInvoiceDetail,
  AccountingSyncedInvoiceSummary,
  getSyncedAccountingCheck,
  getSyncedAccountingCreditMemo,
  getSyncedAccountingCustomer,
  getSyncedAccountingInvoice,
  listSyncedAccountingChecks,
  listSyncedAccountingCreditMemos,
  listSyncedAccountingCustomers,
  listSyncedAccountingInvoices,
} from "@/api/accounting"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "@/components/ui/use-toast"
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"

const PAGE_SIZE = 50

type SyncedTab = "invoices" | "checks" | "customers" | "creditmemos"
type SyncedSortBy = "updated" | "date"
type SyncedSortDir = "asc" | "desc"

export function formatSyncedJson(data: unknown): string {
  if (data === undefined || data === null) {
    return "null"
  }
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return String(data)
  }
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
}

function formatDate(date: string | undefined): string {
  if (!date) {
    return "—"
  }
  return date
}

export function nextDateSortDirection(sort_by: SyncedSortBy, sort_dir: SyncedSortDir): SyncedSortDir {
  if (sort_by !== "date") {
    return "desc"
  }
  return sort_dir === "desc" ? "asc" : "desc"
}

interface RawDataDialogProps {
  open: boolean
  title: string
  summary_lines: string[]
  raw_json: string
  onClose: () => void
}

function RawDataDialog({ open, title, summary_lines, raw_json, onClose }: RawDataDialogProps) {
  async function handle_copy() {
    try {
      await navigator.clipboard.writeText(raw_json)
      toast({ title: "Copied", description: "Raw JSON copied to clipboard." })
    } catch {
      toast({ title: "Copy failed", variant: "destructive" })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(is_open: boolean) => !is_open && onClose()}>
      <DialogContent class="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div class="space-y-3 flex-1 min-h-0 flex flex-col">
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {summary_lines.map((line) => {
              const colon = line.indexOf(": ")
              const label = colon >= 0 ? line.slice(0, colon) : line
              const value = colon >= 0 ? line.slice(colon + 2) : ""
              return (
                <div key={line} class="contents">
                  <dt class="text-gray-500">{label}</dt>
                  <dd class="text-gray-900 truncate">{value}</dd>
                </div>
              )
            })}
          </dl>
          <div class="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => void handle_copy()}>
              Copy JSON
            </Button>
          </div>
          <pre class="text-xs bg-gray-50 border rounded-md p-3 overflow-auto max-h-[60vh] font-mono whitespace-pre-wrap break-all">
            {raw_json}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface StandardSyncedRowProps {
  number: string
  date: string
  customer: string
  amount: number
  remote_id: string
  updated_at: string
  disabled: boolean
  onView: () => void
}

/** Row shared by invoices, payments, and credit memos (number / date / customer / amount layout). */
function StandardSyncedRow({ number, date, customer, amount, remote_id, updated_at, disabled, onView }: StandardSyncedRowProps) {
  return (
    <tr class="hover:bg-gray-50">
      <td class="px-3 py-2 font-medium text-gray-900">{number}</td>
      <td class="px-3 py-2 text-gray-600">{date}</td>
      <td class="px-3 py-2 text-gray-600 max-w-[12rem] truncate">{customer}</td>
      <td class="px-3 py-2 text-right text-gray-900">{formatAmount(amount)}</td>
      <td class="px-3 py-2 text-gray-500 font-mono text-xs max-w-[10rem] truncate">{remote_id}</td>
      <td class="px-3 py-2 text-gray-500 whitespace-nowrap">
        {formatDistanceToNow(parseISO(updated_at), { addSuffix: true })}
      </td>
      <td class="px-3 py-2 text-right">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onView}>
          View raw
        </Button>
      </td>
    </tr>
  )
}

/**
 * A customer's active flag as three states, not two. Customers derived from synced transactions
 * (`derive_accounting_customers`) never learn one — we read the customer off an invoice or payment,
 * not from the provider's contact list — so the column arrives as JSON `null`. `null === undefined`
 * is false, so the old check rendered every derived customer as a confident "No".
 */
function format_active(active: boolean | null | undefined): string {
  if (active === null || active === undefined) {
    return "—"
  }
  return active ? "Yes" : "No"
}

interface CustomerSyncedRowProps {
  name: string
  active?: boolean | null
  remote_id: string
  updated_at: string
  disabled: boolean
  onView: () => void
}

/** Customers have no date/amount, so they use a dedicated name / active layout. */
function CustomerSyncedRow({ name, active, remote_id, updated_at, disabled, onView }: CustomerSyncedRowProps) {
  return (
    <tr class="hover:bg-gray-50">
      <td class="px-3 py-2 font-medium text-gray-900">{name}</td>
      <td class="px-3 py-2 text-gray-600">{format_active(active)}</td>
      <td class="px-3 py-2 text-gray-500 font-mono text-xs max-w-[10rem] truncate">{remote_id}</td>
      <td class="px-3 py-2 text-gray-500 whitespace-nowrap">
        {formatDistanceToNow(parseISO(updated_at), { addSuffix: true })}
      </td>
      <td class="px-3 py-2 text-right">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onView}>
          View raw
        </Button>
      </td>
    </tr>
  )
}

const SYNCED_SEARCH_PLACEHOLDERS: Record<SyncedTab, string> = {
  invoices: "Search invoice #, customer, remote ID…",
  checks: "Search payment #, customer, remote ID…",
  creditmemos: "Search credit memo #, customer, remote ID…",
  customers: "Search customer name, remote ID…",
}

const SYNCED_EMPTY_MESSAGES: Record<SyncedTab, string> = {
  invoices: "No synced invoices found.",
  checks: "No synced payments found.",
  creditmemos: "No synced credit memos found.",
  customers: "No synced customers found.",
}

interface AccountingSyncedDataSectionProps {
  /** When true, omit outer card chrome (e.g. inside integration config SettingsSection). */
  embedded?: boolean
}

export function AccountingSyncedDataSection({ embedded = false }: AccountingSyncedDataSectionProps) {
  const [tab, set_tab] = useState<SyncedTab>("invoices")
  const [sort_by, set_sort_by] = useState<SyncedSortBy>("date")
  const [sort_dir, set_sort_dir] = useState<SyncedSortDir>("desc")
  const [search_input, set_search_input] = useState("")
  const [debounced_q, set_debounced_q] = useState("")
  const [offset, set_offset] = useState(0)
  const [loading, set_loading] = useState(false)
  const [total, set_total] = useState(0)
  const [invoices, set_invoices] = useState<AccountingSyncedInvoiceSummary[]>([])
  const [checks, set_checks] = useState<AccountingSyncedCheckSummary[]>([])
  const [customers, set_customers] = useState<AccountingSyncedCustomerSummary[]>([])
  const [credit_memos, set_credit_memos] = useState<AccountingSyncedCreditMemoSummary[]>([])

  const [dialog_open, set_dialog_open] = useState(false)
  const [dialog_title, set_dialog_title] = useState("")
  const [dialog_summary, set_dialog_summary] = useState<string[]>([])
  const [dialog_json, set_dialog_json] = useState("")
  const [detail_loading, set_detail_loading] = useState(false)

  useEffect(function debounce_search() {
    const timer = setTimeout(() => {
      set_debounced_q(search_input.trim())
      set_offset(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [search_input])

  useEffect(function load_synced_rows() {
    let cancelled = false
    async function load() {
      set_loading(true)
      const params = {
        limit: PAGE_SIZE,
        offset,
        sort_by,
        sort_dir,
        ...(debounced_q ? { q: debounced_q } : {}),
      }
      if (tab === "invoices") {
        const res = await listSyncedAccountingInvoices(params)
        if (cancelled) {
          return
        }
        set_loading(false)
        if (!res.ok) {
          toast({ title: "Failed to load synced data", description: res.error.message, variant: "destructive" })
          return
        }
        set_total(res.value.data.total)
        set_invoices(res.value.data.items)
      } else if (tab === "checks") {
        const res = await listSyncedAccountingChecks(params)
        if (cancelled) {
          return
        }
        set_loading(false)
        if (!res.ok) {
          toast({ title: "Failed to load synced data", description: res.error.message, variant: "destructive" })
          return
        }
        set_total(res.value.data.total)
        set_checks(res.value.data.items)
      } else if (tab === "customers") {
        const res = await listSyncedAccountingCustomers(params)
        if (cancelled) {
          return
        }
        set_loading(false)
        if (!res.ok) {
          toast({ title: "Failed to load synced data", description: res.error.message, variant: "destructive" })
          return
        }
        set_total(res.value.data.total)
        set_customers(res.value.data.items)
      } else {
        const res = await listSyncedAccountingCreditMemos(params)
        if (cancelled) {
          return
        }
        set_loading(false)
        if (!res.ok) {
          toast({ title: "Failed to load synced data", description: res.error.message, variant: "destructive" })
          return
        }
        set_total(res.value.data.total)
        set_credit_memos(res.value.data.items)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [tab, debounced_q, offset, sort_by, sort_dir])

  function handle_tab_change(next: SyncedTab) {
    set_tab(next)
    set_offset(0)
  }

  function handle_date_sort() {
    const next_sort_dir = nextDateSortDirection(sort_by, sort_dir)
    set_sort_by("date")
    set_sort_dir(next_sort_dir)
    set_offset(0)
  }

  async function open_invoice_raw(row: AccountingSyncedInvoiceSummary) {
    set_detail_loading(true)
    set_dialog_open(true)
    set_dialog_title(`Invoice ${row.invoice_number}`)
    set_dialog_summary([
      `Remote ID: ${row.remote_id}`,
      `Customer: ${row.customer_name ?? "—"}`,
      `Amount: ${formatAmount(row.total_amount)}`,
      `Status: ${row.status ?? "—"}`,
    ])
    set_dialog_json("Loading…")
    const res = await getSyncedAccountingInvoice(row.id)
    set_detail_loading(false)
    if (!res.ok) {
      toast({ title: "Failed to load raw data", description: res.error.message, variant: "destructive" })
      set_dialog_open(false)
      return
    }
    const detail: AccountingSyncedInvoiceDetail = res.value.data
    set_dialog_json(formatSyncedJson(detail.data))
  }

  async function open_check_raw(row: AccountingSyncedCheckSummary) {
    set_detail_loading(true)
    set_dialog_open(true)
    set_dialog_title(`Payment ${row.check_number}`)
    set_dialog_summary([
      `Remote ID: ${row.remote_id}`,
      `Customer: ${row.customer_name ?? "—"}`,
      `Amount: ${formatAmount(row.total_amount)}`,
      `Method: ${row.payment_method ?? "—"}`,
    ])
    set_dialog_json("Loading…")
    const res = await getSyncedAccountingCheck(row.id)
    set_detail_loading(false)
    if (!res.ok) {
      toast({ title: "Failed to load raw data", description: res.error.message, variant: "destructive" })
      set_dialog_open(false)
      return
    }
    const detail: AccountingSyncedCheckDetail = res.value.data
    set_dialog_json(formatSyncedJson(detail.data))
  }

  async function open_customer_raw(row: AccountingSyncedCustomerSummary) {
    set_detail_loading(true)
    set_dialog_open(true)
    set_dialog_title(`Customer ${row.display_name}`)
    set_dialog_summary([
      `Remote ID: ${row.remote_id}`,
      `Name: ${row.display_name}`,
      `Active: ${format_active(row.active)}`,
    ])
    set_dialog_json("Loading…")
    const res = await getSyncedAccountingCustomer(row.id)
    set_detail_loading(false)
    if (!res.ok) {
      toast({ title: "Failed to load raw data", description: res.error.message, variant: "destructive" })
      set_dialog_open(false)
      return
    }
    const detail: AccountingSyncedCustomerDetail = res.value.data
    set_dialog_json(formatSyncedJson(detail.data))
  }

  async function open_credit_memo_raw(row: AccountingSyncedCreditMemoSummary) {
    set_detail_loading(true)
    set_dialog_open(true)
    set_dialog_title(`Credit Memo ${row.doc_number ?? row.remote_id}`)
    set_dialog_summary([
      `Remote ID: ${row.remote_id}`,
      `Doc Number: ${row.doc_number ?? "—"}`,
      `Customer: ${row.customer_name ?? "—"}`,
      `Amount: ${formatAmount(row.total_amount)}`,
    ])
    set_dialog_json("Loading…")
    const res = await getSyncedAccountingCreditMemo(row.id)
    set_detail_loading(false)
    if (!res.ok) {
      toast({ title: "Failed to load raw data", description: res.error.message, variant: "destructive" })
      set_dialog_open(false)
      return
    }
    const detail: AccountingSyncedCreditMemoDetail = res.value.data
    set_dialog_json(formatSyncedJson(detail.data))
  }

  const page_start = total === 0 ? 0 : offset + 1
  const page_end = Math.min(offset + PAGE_SIZE, total)
  const can_prev = offset > 0
  const can_next = offset + PAGE_SIZE < total
  const col_count = tab === "customers" ? 5 : 7
  const current_rows =
    tab === "invoices" ? invoices : tab === "checks" ? checks : tab === "creditmemos" ? credit_memos : customers
  const is_empty = current_rows.length === 0
  const date_sort_label = sort_by === "date" ? (sort_dir === "desc" ? "Date descending" : "Date ascending") : "Sort by date"
  const DateSortIcon = sort_by === "date" ? (sort_dir === "desc" ? ArrowDown : ArrowUp) : ArrowUpDown

  const content = (
    <>
      {!embedded && (
        <div>
          <h2 class="text-lg font-semibold leading-7 text-gray-900">Synced data</h2>
          <p class="text-sm text-gray-500 mt-1">
            Browse invoices, payments, credit memos, and customers imported from your accounting system. View raw
            provider JSON for debugging.
          </p>
        </div>
      )}
      {embedded && (
        <p class="text-sm text-gray-500">
          Browse invoices, payments, credit memos, and customers imported from your accounting system. View raw provider
          JSON for debugging.
        </p>
      )}

      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={tab === "invoices" ? "default" : "outline"}
          onClick={() => handle_tab_change("invoices")}>
          Invoices
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "checks" ? "default" : "outline"}
          onClick={() => handle_tab_change("checks")}>
          Payments
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "creditmemos" ? "default" : "outline"}
          onClick={() => handle_tab_change("creditmemos")}>
          Credit Memos
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === "customers" ? "default" : "outline"}
          onClick={() => handle_tab_change("customers")}>
          Customers
        </Button>
      </div>

      <Input
        type="search"
        placeholder={SYNCED_SEARCH_PLACEHOLDERS[tab]}
        value={search_input}
        onInput={(e) => set_search_input((e.target as HTMLInputElement).value)}
        class="max-w-md"
      />

      <div class="overflow-x-auto border rounded-lg">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-600">
            {tab === "customers" ? (
              <tr>
                <th class="px-3 py-2 font-medium">Name</th>
                <th class="px-3 py-2 font-medium">Active</th>
                <th class="px-3 py-2 font-medium">Remote ID</th>
                <th class="px-3 py-2 font-medium">Updated</th>
                <th class="px-3 py-2 font-medium" />
              </tr>
            ) : (
              <tr>
                <th class="px-3 py-2 font-medium">Number</th>
                <th class="px-3 py-2 font-medium">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="h-7 px-1.5 -ml-1.5 gap-1 text-gray-600 hover:text-gray-900"
                    aria-label={date_sort_label}
                    title={date_sort_label}
                    onClick={handle_date_sort}>
                    Date
                    <DateSortIcon class="h-3.5 w-3.5" />
                  </Button>
                </th>
                <th class="px-3 py-2 font-medium">Customer</th>
                <th class="px-3 py-2 font-medium text-right">Amount</th>
                <th class="px-3 py-2 font-medium">Remote ID</th>
                <th class="px-3 py-2 font-medium">Updated</th>
                <th class="px-3 py-2 font-medium" />
              </tr>
            )}
          </thead>
          <tbody class="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={col_count} class="px-3 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : is_empty ? (
              <tr>
                <td colSpan={col_count} class="px-3 py-8 text-center text-gray-500">
                  {SYNCED_EMPTY_MESSAGES[tab]}
                </td>
              </tr>
            ) : tab === "invoices" ? (
              invoices.map((row) => (
                <StandardSyncedRow
                  key={row.id}
                  number={row.invoice_number}
                  date={formatDate(row.invoice_date)}
                  customer={row.customer_name ?? "—"}
                  amount={row.total_amount}
                  remote_id={row.remote_id}
                  updated_at={row.updated_at}
                  disabled={detail_loading}
                  onView={() => void open_invoice_raw(row)}
                />
              ))
            ) : tab === "checks" ? (
              checks.map((row) => (
                <StandardSyncedRow
                  key={row.id}
                  number={row.check_number}
                  date={formatDate(row.check_date)}
                  customer={row.customer_name ?? "—"}
                  amount={row.total_amount}
                  remote_id={row.remote_id}
                  updated_at={row.updated_at}
                  disabled={detail_loading}
                  onView={() => void open_check_raw(row)}
                />
              ))
            ) : tab === "creditmemos" ? (
              credit_memos.map((row) => (
                <StandardSyncedRow
                  key={row.id}
                  number={row.doc_number ?? "—"}
                  date={formatDate(row.txn_date)}
                  customer={row.customer_name ?? "—"}
                  amount={row.total_amount}
                  remote_id={row.remote_id}
                  updated_at={row.updated_at}
                  disabled={detail_loading}
                  onView={() => void open_credit_memo_raw(row)}
                />
              ))
            ) : (
              customers.map((row) => (
                <CustomerSyncedRow
                  key={row.id}
                  name={row.display_name}
                  active={row.active}
                  remote_id={row.remote_id}
                  updated_at={row.updated_at}
                  disabled={detail_loading}
                  onView={() => void open_customer_raw(row)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
        <span>
          {total === 0 ? "No rows" : `Showing ${page_start}–${page_end} of ${total.toLocaleString()}`}
        </span>
        <div class="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={!can_prev || loading} onClick={() => set_offset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!can_next || loading} onClick={() => set_offset(offset + PAGE_SIZE)}>
            Next
          </Button>
        </div>
      </div>

      <RawDataDialog
        open={dialog_open}
        title={dialog_title}
        summary_lines={dialog_summary}
        raw_json={dialog_json}
        onClose={() => set_dialog_open(false)}
      />
    </>
  )

  if (embedded) {
    return <div class="space-y-4">{content}</div>
  }

  return (
    <div id="accounting-synced-data" class="bg-white rounded-lg shadow-sm p-6 space-y-4 scroll-mt-6">
      {content}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("format_active", () => {
    // The server sends JSON `null` for a customer derived from transactions. `null === undefined`
    // is false, so the old check rendered all 949 of one org's customers as a confident "No".
    it("shows an unknown status as unknown, not as No", () => {
      expect(format_active(null)).toBe("—")
      expect(format_active(undefined)).toBe("—")
    })

    it("still reports a known status", () => {
      expect(format_active(true)).toBe("Yes")
      expect(format_active(false)).toBe("No")
    })
  })

  describe("formatSyncedJson", () => {
    it("formats objects with indentation", () => {
      expect(formatSyncedJson({ a: 1 })).toBe('{\n  "a": 1\n}')
    })

    it("returns null for nullish values", () => {
      expect(formatSyncedJson(null)).toBe("null")
      expect(formatSyncedJson(undefined)).toBe("null")
    })
  })

  describe("nextDateSortDirection", () => {
    it("starts date sorting newest first", () => {
      expect(nextDateSortDirection("updated", "desc")).toBe("desc")
    })

    it("toggles active date sorting", () => {
      expect(nextDateSortDirection("date", "desc")).toBe("asc")
      expect(nextDateSortDirection("date", "asc")).toBe("desc")
    })
  })
}
